import React, { ComponentType, useEffect, useState } from "react";
import {
  useForm,
  Controller,
  useFieldArray,
  UseFormRegister,
} from "react-hook-form";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Stack,
  Button,
  useDisclosure,
  GridItem,
  Text,
  Box,
  useToast,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import ImportTranscriptModal, {
  TranscriptSubmitFn,
} from "./components/ImportTranscriptModal";
import TranscriptLines from "./components/TranscriptLines";
import { QueryResultProps } from "../../types/shared";
import {
  AdminInsightsProductInfo,
  InsightsProduct,
  InsightsProductInput,
  Product,
} from "../../graphql/fragments/product";
import DatePicker from "./components/DatePicker";
import ErrorAlert from "./components/ErrorAlert";
import FileInput from "./components/FileInput";
import FileUploadProgressModal from "./components/FileUploadProgressModal";
import {
  InsightsProductFormInput,
  FileMap,
  FileType,
  FileMapField,
  TranscriptLine,
} from "./insightsProduct";
import moment from "moment";
import useUploader from "./hooks/useUploader";
import SpeakerContainer from "./components/SpeakerContainer";
import { parseTranscriptContent, Vendor, Sector } from "./insightsProduct";
import TopicSelect, { TopicSelectValue } from "./components/TopicSelect";
import { withRouter, RouteComponentProps } from "react-router";
import { MarketplaceControlsProps } from "./components/MarketplaceControls";
import gql from "graphql-tag";
import { Mutation, MutationFn, withApollo } from "react-apollo";
import axios from "axios";
import ApolloClient from "apollo-client";

interface TranscriptState {
  parsed: TranscriptLine[] | null;
  raw: string | null;
  parseError: Error | null;
  speakers: string[] | null;
}

const initialTranscriptState = {
  parsed: null,
  raw: null,
  parseError: null,
  speakers: null,
};

const filePatterns = {
  transcript: /-\s?Transcript\s?-.+\.pdf/,
  abstract: /-\s?TVA\s?-.+\.pdf/,
  replay: /-\s?Replay\s?-.+\.mp4/,
  eventDeck: /-\s?Event Deck\s?-.+\.pdf/,
};

const filesArrayToFileMap = (fileNames: string[]): FileMap => {
  const defaultFieldValue = null;

  const fileMap = {
    transcript: defaultFieldValue,
    abstract: defaultFieldValue,
    replay: defaultFieldValue,
    eventDeck: defaultFieldValue,
  };

  if (!fileNames) return fileMap;

  for (const name of fileNames) {
    for (const [type, regex] of Object.entries(filePatterns)) {
      if (name.match(regex)) {
        fileMap[type] = {
          name,
          file: null,
        };
      }
    }
  }

  return fileMap;
};

interface InsightsProductFormProps
  extends QueryResultProps,
    RouteComponentProps {
  product: InsightsProduct | null;
  marketplaceFlags: MarketplaceControlsProps["marketplaceFlags"];
  mutation: CreateUpdateInsightsMutation;
  productImageFile: File | null;
  client: ApolloClient<any>;
}

const uploadProductImage = async (
  file: File | null,
  url: string,
  name: string
) => {
  if (file && file.size) {
    const headers = {
      "Content-Type": "multipart/form-data",
    };
    const formData = new FormData();
    formData.append(name, file);
    return await axios.post(url, formData, {
      headers,
    });
  }
  return null;
};

const validateUri = (uri: string) => {
  if (uri === "") {
    return;
  }
  try {
    new URL(uri);
    return;
  } catch {
    return "Signup link must be a valid url.";
  }
};

const TranscriptAndSubmissionSection = (props: {
  register: UseFormRegister<InsightsProductFormInput>;
  transcript: TranscriptState;
  onClick: () => void;
}) => {
  const { register, transcript, onClick } = props;
  return (
    <GridItem colSpan={{ base: 5, lg: 4 }} marginTop={4}>
      <Stack spacing={4}>
        <FormControl>
          <FormLabel>Teaser Quote #1</FormLabel>
          <Input
            {...register("eventDetails.teaserQuotes.0")}
            placeholder="Teaser Quote #1"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Teaser Quote #2</FormLabel>
          <Input
            {...register("eventDetails.teaserQuotes.1")}
            placeholder="Teaser Quote #2"
          />
        </FormControl>
        <TranscriptLines transcriptLines={transcript.parsed} />
        <Button colorScheme="pink" onClick={onClick}>
          Import Transcript
        </Button>
        <Button colorScheme="blue" type="submit">
          Submit
        </Button>
      </Stack>
    </GridItem>
  );
};

const InsightsProductForm: React.FC<InsightsProductFormProps> = (props) => {
  const {
    product,
    mutation,
    history,
    marketplaceFlags,
    productImageFile,
    client,
  } = props;
  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    setValue,
    watch,
    control,
  } = useForm<InsightsProductFormInput>({
    defaultValues: {
      name: product?.name || "",
      description: product?.description || "",
      previewDescription: product?.previewDescription || "",
      publishDate: product?.publishDate || moment().toISOString(),
      isAvailable: product?.isAvailable ?? true,
      fileMap: filesArrayToFileMap(product?.files || []),
      eventDetails: {
        date: product?.eventDetails?.date || moment().toISOString(),
        time:
          product?.eventDetails?.time ||
          moment()
            .add(moment().minute() > 30 ? 1 : 0, "hours")
            .minutes(moment().minute() <= 30 ? 30 : 0)
            .format("LT"),
        location: product?.eventDetails?.location || "",
        address: product?.eventDetails?.address || "",
        type: product?.eventDetails?.type || "",
        description: product?.eventDetails?.description || "",
        signupUri: product?.eventDetails?.signupUri || "",
        speakers:
          // map to omit __typename property
          product?.eventDetails?.speakers.map((s) => ({
            name: s.name,
            title: s.title,
          })) || [{ name: null, title: null }],
        organization: product?.eventDetails?.organization || "",
        teaserQuotes: product?.eventDetails?.teaserQuotes || [],
      },
      // map to omit __typename property
      vendors:
        product?.vendors.map((v) => ({
          vendorId: v.vendorId,
          slug: v.slug,
          name: v.name,
        })) || [],
      sectors:
        product?.sectors.map((s) => ({
          sectorId: s.sectorId,
          slug: s.slug,
          name: s.name,
        })) || [],
    },
  });

  const toast = useToast({ duration: 9000, isClosable: true });

  const {
    fields: speakerFields,
    append: appendSpeaker,
    remove: removeSpeaker,
  } = useFieldArray({
    name: "eventDetails.speakers",
    control,
  });

  const resetFileMapEntry = (key: string): void => {
    const fileMap = getValues("fileMap");
    setValue("fileMap", {
      ...fileMap,
      [key]: {
        name: fileMap[key].name,
        file: null,
      },
    });
  };

  const {
    isOpen: isTranscriptModalOpen,
    onClose: onTranscriptModalClose,
    onOpen: OnTranscriptModalOpen,
  } = useDisclosure();

  const [transcript, setTranscript] = useState<TranscriptState>(
    initialTranscriptState
  );

  useEffect(() => {
    if (product?.transcript && product?.transcriptSpeakers?.length) {
      const { transcriptSpeakers, transcript } = product;
      const transcriptLines = parseTranscriptContent(
        transcriptSpeakers,
        transcript
      );
      setTranscript({
        speakers: transcriptSpeakers,
        raw: transcript,
        parsed: transcriptLines,
        parseError: null,
      });
    }
  }, []);

  const [isUploadProgressModalOpen, setIsUploadProgressModalOpen] =
    useState(false);
  const uploader = useUploader();
  useEffect(() => {
    if (Object.keys(uploader.state).length && !isUploadProgressModalOpen) {
      setIsUploadProgressModalOpen(true);
    }
    for (const [key, value] of Object.entries(uploader.state)) {
      if (value.done) {
        resetFileMapEntry(key);
      }
    }
  }, [uploader.state]);

  const [createUpdateError, setCreateUpdateError] = useState<string | null>(
    null
  );

  // success redirect
  const [successRedirectPath, setSuccessRedirectPath] = useState<string | null>(
    null
  );
  useEffect(() => {
    if (!isUploadProgressModalOpen && successRedirectPath) {
      setTimeout(() => history.push(successRedirectPath), 150);
    }
  }, [successRedirectPath, isUploadProgressModalOpen]);

  const onFileUploadProgressModalClose = () => {
    if (successRedirectPath) {
      history.push(successRedirectPath);
    } else {
      setIsUploadProgressModalOpen(false);
    }
  };

  // pending file count
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const watchFiles = watch([
    "fileMap.transcript",
    "fileMap.abstract",
    "fileMap.replay",
    "fileMap.eventDeck",
  ]);
  useEffect(() => {
    const count = watchFiles.filter((fields) => !!fields?.file).length;
    setPendingUploadCount(count);
  }, [watchFiles]);

  // form submission
  const onSubmit = async (data: InsightsProductFormInput) => {
    const { fileMap, ...product } = data;

    const { files } = await uploader.uploadFiles(fileMap);

    const mutationData = {
      variables: {
        product: {
          ...product,
          ...marketplaceFlags,
          files,
          transcriptLines: transcript.parsed,
          transcriptSpeakers: transcript.speakers,
          transcriptRaw: transcript.raw,
          eventDetails: {
            ...product.eventDetails,
            speakers: product.eventDetails.speakers.filter(
              (s) => !!(s.name?.trim() && s.title?.trim())
            ),
          },
        },
      },
    };

    let response = null;
    try {
      const createUpdateResponse = await mutation(mutationData);
      if (createUpdateResponse?.data) {
        if ("updateInsightsProduct" in createUpdateResponse.data) {
          response = createUpdateResponse.data?.updateInsightsProduct;
        }

        if ("createInsightsProduct" in createUpdateResponse.data) {
          response = createUpdateResponse.data?.createInsightsProduct;
        }
      }

      if (!response) {
        throw new Error("product response is empty");
      }
    } catch (err) {
      console.log(err);
      setCreateUpdateError(
        "An unknown error occurred while trying to create or update this product."
      );
      return;
    }

    // error response from server
    const message = response.error;
    if (message) {
      return setCreateUpdateError(message);
    }

    try {
      if (productImageFile) {
        const res = await uploadProductImage(
          productImageFile,
          `/upload/product/image/${response.product.id}`,
          "image"
        );

        await client.mutate({
          mutation: gql`
            mutation updateProductImage($id: String!, $name: String!) {
              updateProductImage(id: $id, name: $name)
            }
          `,
          variables: {
            id: response.product.id,
            name: res?.data.fileName,
          },
        });
      }
    } catch (err) {
      console.log(err);
      toast({
        title: "Error",
        description: "An error occurred during image file upload.",
        status: "error",
      });
      return;
    }

    const productResponse = response?.product;
    // success
    if (productResponse) {
      return setSuccessRedirectPath(`/products?q=${productResponse.id}`);
    }

    // no response and no errors caught errors caught
    setCreateUpdateError(
      "An unknown error occurred while trying to update this product."
    );
  };

  const validateFile = (type: FileType) => {
    return (file: FileMapField | null) => {
      if (!file || !file?.name) return;
      const fileName = file.name;
      const isPatternMatch = fileName?.match(filePatterns[type]);

      if (!isPatternMatch) {
        return `File name "${fileName}" is invalid for this file type.`;
      }
      return;
    };
  };

  const onTranscriptModalSubmit: TranscriptSubmitFn = ({
    raw,
    parsed,
    parseError,
    speakers,
  }) => {
    if (parseError) {
      setTranscript({ raw: null, parsed: null, speakers: null, parseError });
    } else {
      setTranscript({ raw, parsed, speakers, parseError: null });
    }
  };

  const handleTopicChange =
    (
      type: "Vendor" | "Sector",
      onChange: (value: Vendor[] | Sector[]) => void
    ) =>
    (options: TopicSelectValue | null) => {
      options = options || [];

      if (type === "Vendor") {
        onChange(
          options.map((opt) => ({
            name: opt.name,
            slug: opt.slug,
            vendorId: opt.id,
          }))
        );
      }

      if (type === "Sector") {
        onChange(
          options.map((opt) => ({
            name: opt.name,
            slug: opt.slug,
            sectorId: opt.id,
          }))
        );
      }
    };

  const resetTranscriptState = (): void => {
    setTranscript({
      raw: null,
      parsed: null,
      parseError: null,
      speakers: null,
    });
  };

  return (
    <>
      <ErrorAlert
        header="Error"
        body="A problem occurred while parsing the transcript content, please review
        the transcript content and try again."
        isOpen={!!transcript.parseError}
        onConfirm={resetTranscriptState}
      />
      <ErrorAlert
        header="Error"
        body={createUpdateError || ""}
        isOpen={!!createUpdateError}
        onConfirm={() => setCreateUpdateError(null)}
      />
      <ImportTranscriptModal
        isOpen={isTranscriptModalOpen}
        onClose={onTranscriptModalClose}
        onSubmit={onTranscriptModalSubmit}
        transcript={product?.transcript || ""}
        speakers={product?.transcriptSpeakers?.join(", ") || ""}
      />
      <FileUploadProgressModal
        onClose={() => onFileUploadProgressModalClose()}
        isOpen={isUploadProgressModalOpen}
        uploadState={uploader.state}
      />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          <FormControl isInvalid={!!errors.name} isRequired>
            <FormLabel>Name</FormLabel>
            <Input
              {...register("name", { required: "Name is required" })}
              placeholder="Name"
              data-cy="name"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.description} isRequired>
            <FormLabel>Description</FormLabel>
            <Textarea
              rows={5}
              {...register("description", {
                required: "Description is required",
              })}
              placeholder="Description"
              data-cy="description"
            />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.previewDescription} isRequired>
            <FormLabel>Preview Description</FormLabel>
            <Input
              {...register("previewDescription", {
                required: "Preview Description is required",
              })}
              placeholder="Preview Description"
              data-cy="preview-description"
            />
            <FormErrorMessage>
              {errors.previewDescription?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.publishDate} isRequired>
            <FormLabel>Publish Date</FormLabel>
            <Controller
              control={control}
              name="publishDate"
              rules={{
                required: "Publish Date is required",
              }}
              render={({ field: { onChange, value, name, ref } }) => (
                <DatePicker
                  inputRef={ref}
                  name={name}
                  onChange={(date) => {
                    if (date) onChange(moment(date).toISOString());
                    else onChange(null);
                  }}
                  selected={value ? moment(value).toDate() : undefined}
                />
              )}
            />
            <FormErrorMessage>{errors.publishDate?.message}</FormErrorMessage>
          </FormControl>

          <Stack spacing={4}>
            <FormLabel>Speakers</FormLabel>
            {speakerFields.map((field, index) => (
              <SpeakerContainer
                onRemoveClick={() => removeSpeaker(index)}
                showCloseButtons={speakerFields.length > 1}
                key={field.id}
              >
                <FormControl
                  isRequired
                  isInvalid={!!errors.eventDetails?.speakers?.[index]?.title}
                >
                  <FormLabel>Title</FormLabel>
                  <Input
                    {...register(`eventDetails.speakers.${index}.title`, {
                      required: "Title is required",
                    })}
                    placeholder="Title"
                    data-cy={`speaker-title-${index}`}
                  />
                  <FormErrorMessage>
                    {errors.eventDetails?.speakers?.[index]?.title?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={!!errors.eventDetails?.speakers?.[index]?.name}
                >
                  <FormLabel>Name</FormLabel>
                  <Input
                    {...register(`eventDetails.speakers.${index}.name`, {
                      required: "Name is required",
                    })}
                    placeholder="Name"
                    data-cy={`speaker-name-${index}`}
                  />
                  <FormErrorMessage>
                    {errors.eventDetails?.speakers?.[index]?.name?.message}
                  </FormErrorMessage>
                </FormControl>
              </SpeakerContainer>
            ))}
            <Button
              isFullWidth={false}
              leftIcon={<AddIcon />}
              onClick={() => appendSpeaker({ title: null, name: null })}
              colorScheme="teal"
            >
              Add Speaker
            </Button>
          </Stack>

          <FormControl>
            <FormLabel>Vendors</FormLabel>
            <Controller
              name="vendors"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <TopicSelect
                    type="Vendor"
                    value={value}
                    onChange={handleTopicChange("Vendor", onChange)}
                  />
                );
              }}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Sectors</FormLabel>
            <Controller
              name="sectors"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <TopicSelect
                    type="Sector"
                    value={value}
                    onChange={handleTopicChange("Sector", onChange)}
                  />
                );
              }}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Event Details</FormLabel>
            <Stack borderWidth={1} borderRadius={5} p={5} spacing={5}>
              <FormControl isInvalid={!!errors.eventDetails?.date} isRequired>
                <FormLabel>Date</FormLabel>
                <Controller
                  name="eventDetails.date"
                  control={control}
                  rules={{
                    required: "Date is required",
                  }}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <DatePicker
                      inputRef={ref}
                      name={name}
                      onChange={(date) => {
                        if (date) onChange(moment(date).toISOString());
                        else onChange(null);
                      }}
                      selected={value ? moment(value).toDate() : undefined}
                    />
                  )}
                />
                <FormErrorMessage>
                  {errors.eventDetails?.date?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.eventDetails?.time} isRequired>
                <FormLabel>Time (EST)</FormLabel>
                <Controller
                  name={"eventDetails.time"}
                  control={control}
                  rules={{
                    required: "Time is required",
                  }}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <DatePicker
                      inputRef={ref}
                      name={name}
                      onChange={(date) =>
                        onChange(date ? moment(date).format("h:mm A") : null)
                      }
                      initialFormat={"h:mm A"}
                      selected={
                        value ? moment(value, "h:mm A").toDate() : undefined
                      }
                      pickerProps={{
                        showTimeSelect: true,
                        showTimeSelectOnly: true,
                        timeIntervals: 30,
                        timeCaption: "Time",
                        dateFormat: "h:mm a",
                      }}
                    />
                  )}
                />
                <FormErrorMessage>
                  {errors.eventDetails?.time?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.eventDetails?.location}
                isRequired
              >
                <FormLabel>Location</FormLabel>
                <Input
                  {...register("eventDetails.location", {
                    required: "Location is required",
                  })}
                  placeholder="Virtual or physical event location"
                  data-cy="event-location"
                />
                <FormErrorMessage>
                  {errors.eventDetails?.location?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.eventDetails?.address}
                isRequired
              >
                <FormLabel>Address</FormLabel>
                <Input
                  {...register("eventDetails.address", {
                    required: "Address is required",
                  })}
                  placeholder="Address"
                  data-cy="event-address"
                />
                <FormErrorMessage>
                  {errors.eventDetails?.address?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.eventDetails?.type} isRequired>
                <FormLabel>Type</FormLabel>
                <Input
                  {...register("eventDetails.type", {
                    required: "Type is required",
                  })}
                  placeholder="Type"
                  data-cy="event-type"
                />
                <FormErrorMessage>
                  {errors.eventDetails?.type?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.eventDetails?.description}
                isRequired
              >
                <FormLabel>Description</FormLabel>
                <Input
                  {...register("eventDetails.description", {
                    required: "Description is required",
                  })}
                  placeholder="Description"
                  data-cy="event-description"
                />
                <FormErrorMessage>
                  {errors.eventDetails?.description?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={!!errors.eventDetails?.organization}
                isRequired
              >
                <FormLabel>Organization</FormLabel>
                <Input
                  {...register("eventDetails.organization", {
                    required: "Organization is required",
                  })}
                  placeholder="Organization"
                  data-cy="event-organization"
                />
                <FormErrorMessage>
                  {errors.eventDetails?.organization?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.eventDetails?.signupUri}>
                <FormLabel>Sign-up Link</FormLabel>
                <Input
                  {...register("eventDetails.signupUri", {
                    validate: validateUri,
                  })}
                  placeholder={"https://us02web.zoom.us/webinar/register/"}
                  data-cy="event-signup-uri"
                />
                <FormErrorMessage>
                  {errors.eventDetails?.signupUri?.message}
                </FormErrorMessage>
              </FormControl>
            </Stack>
          </FormControl>

          <Box>
            <FormLabel>Files</FormLabel>
            <Stack borderWidth={1} borderRadius={5} p={5} spacing={5}>
              <FormControl isInvalid={!!errors.fileMap?.transcript}>
                <FormLabel>Transcript</FormLabel>
                <Controller
                  name={"fileMap.transcript"}
                  control={control}
                  rules={{
                    validate: validateFile("transcript"),
                  }}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FileInput
                        accept="application/pdf"
                        onChange={onChange}
                        value={value}
                      />
                    );
                  }}
                />
                <FormErrorMessage>
                  {errors.fileMap?.transcript?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.fileMap?.abstract}>
                <FormLabel>Abstract</FormLabel>
                <Controller
                  name={"fileMap.abstract"}
                  control={control}
                  rules={{
                    validate: validateFile("abstract"),
                  }}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FileInput
                        accept="application/pdf"
                        onChange={onChange}
                        value={value}
                      />
                    );
                  }}
                />
                <FormErrorMessage>
                  {errors.fileMap?.abstract?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.fileMap?.replay}>
                <FormLabel>Replay</FormLabel>
                <Controller
                  name={"fileMap.replay"}
                  control={control}
                  rules={{
                    validate: validateFile("replay"),
                  }}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FileInput
                        accept="video/mp4"
                        onChange={onChange}
                        value={value}
                      />
                    );
                  }}
                />
                <FormErrorMessage>
                  {errors.fileMap?.replay?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.fileMap?.eventDeck}>
                <FormLabel>Event Deck</FormLabel>
                <Controller
                  name={"fileMap.eventDeck"}
                  control={control}
                  rules={{
                    validate: validateFile("eventDeck"),
                  }}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FileInput
                        accept="application/pdf"
                        onChange={onChange}
                        value={value}
                      />
                    );
                  }}
                />
                <FormErrorMessage>
                  {errors.fileMap?.eventDeck?.message}
                </FormErrorMessage>
              </FormControl>
              <Text
                size="sm"
                color="gray.500"
              >{`${pendingUploadCount} files pending for upload`}</Text>
            </Stack>
          </Box>
        </Stack>
        <TranscriptAndSubmissionSection
          register={register}
          transcript={transcript}
          onClick={OnTranscriptModalOpen}
        />
      </form>
    </>
  );
};

const createInsightsProductMutation = gql`
  mutation CreateInsightsProduct($product: InsightsProductInput!) {
    createInsightsProduct(product: $product) {
      product {
        ...AdminInsightsProductInfo
      }
      error
    }
  }
  ${AdminInsightsProductInfo}
`;

const updateInsightsProductMutation = gql`
  mutation UpdateInsightsProduct(
    $id: String!
    $product: InsightsProductInput!
  ) {
    updateInsightsProduct(id: $id, product: $product) {
      product {
        ...AdminInsightsProductInfo
      }
      error
    }
  }
  ${AdminInsightsProductInfo}
`;

type CreateUpdateInsightsKeys =
  | "createInsightsProduct"
  | "updateInsightsProduct";

type CreateUpdateInsightsResponse = {
  product: Product;
  error: string;
};

type CreateUpdateInsightsMutation = MutationFn<
  Record<CreateUpdateInsightsKeys, CreateUpdateInsightsResponse>,
  {
    product: InsightsProductInput;
  }
>;

type InsightsProductFormWithDataProps = Omit<
  InsightsProductFormProps,
  "mutation" | "client"
>;

export default withRouter<
  InsightsProductFormWithDataProps,
  ComponentType<InsightsProductFormWithDataProps>
>(
  withApollo((props) => {
    const id = props.product?.id;
    return (
      <Mutation
        mutation={
          id ? updateInsightsProductMutation : createInsightsProductMutation
        }
        variables={id ? { id } : undefined}
      >
        {(mutation: CreateUpdateInsightsMutation) => {
          return <InsightsProductForm {...props} mutation={mutation} />;
        }}
      </Mutation>
    );
  })
);
